<template>
  <div class="task-replay-wrap">
    <Search @replay="search" :rules="rules" :replay-loading="loading" />
    <DeviceMap class="replay-map" ref="deviceMap" :map-config="mapConfig" key="replay">
      <AlarmPopup v-if="alarmInfos && alarmInfos.length" :data="alarmInfos" />
      <ProgressBar
        v-if="replaying"
        :speed="speed"
        :range="range"
        :cache-ranges="cacheRanges"
        @change="handleChange"
        @jumpto="handleJumpto"
      />

      <div class="control-wrap" v-if="taskId">
        <GradientBar v-if="mapConfig" :max="max" :gradient="mapConfig.gradient" />
      </div>
    </DeviceMap>
  </div>
</template>

<script>
import Search from './components/search';
import AlarmPopup from './components/alarm-popup.vue';
import DeviceMap from '../../task/deviceMap.vue';
import GradientBar from '@/components/gradientBar';
import ProgressBar from '@/components/progressBar';

import { formatTime, getTime, toTimestamp } from '@/utils/date';

import { replay, getDevicemapParam } from '@/api/task';

import { validParamObject } from '@/utils/validator';
import { cloneDeep } from 'lodash';
import { isArray, isObject } from '@/utils/is';
import { convexClosure, polygonScale } from '@/utils/algorithm';
import { timer } from '@/utils/utils';
import { stopReplay } from '@/api/devices';
import { subscribe } from '@/utils/mqtt';

function search(nums, [min, max]) {
  let left = 0;
  let right = nums.length - 1;
  let mid = -1;
  while (left <= right) {
    mid = Math.floor((right - left) / 2) + left;
    const num = nums[mid];
    if (num > min && num < max) {
      return mid;
    } else if (num > max) {
      right = mid - 1;
    } else {
      left = mid + 1;
    }
  }
  return right;
}

export default {
  components: { Search, DeviceMap, AlarmPopup, GradientBar, ProgressBar },
  data() {
    return {
      model: null,
      taskId: '',
      loading: false,
      alarmInfos: null,
      logTime: 0,
      replaying: false,
      max: 100,
      mapConfig: null,
      rules: {
        taskId: [{ required: true, message: '请选择任务', trigger: 'change' }]
      },
      currentIndex: -1,
      dataCache: {},
      cacheRanges: []
    };
  },
  computed: {
    subscribeKey() {
      // return this.taskId ? `/socket/devicemap/detail/${this.taskId}` : '';
      // return '/socket/devicemap/replay/detail';
      return '/{prefix}/{userId}/devicemap/replay/detail/';
    },
    speed() {
      return (this.model && this.model.playSpeed) || 10;
    },
    range() {
      return this.model ? this.model.rangeDateVal : [];
    }
  },
  provide() {
    return {
      alarmReplay: this.alarmReplay.bind(this)
    };
  },
  methods: {
    matchData(time) {
      let multiple = 0.5 * this.speed;
      multiple = multiple < 1.5 ? 1.5 : multiple;
      let boundary = getTime('s') * multiple;
      const range = [time - boundary, time];
      const times = Object.keys(this.dataCache);
      const index = search(times, range);
      if (index === -1 || this.currentIndex === index) return null;
      this.currentIndex = index;
      return this.dataCache[times[index]];
    },
    handleChange(time) {
      const res = this.matchData(time);
      if (!res) return;
      this.$refs.deviceMap.upBasicData(res);
      this.$refs.deviceMap.updateCallback(() => {
        this.max = res.max;
      });
      res.time && this.setLogTime(res.time);
    },
    handleJumpto() {
      const $map = this.$refs.deviceMap;
      timer(() => $map.updateInterval());
    },
    async search(model) {
      this.loading = true;
      this.replaying = false;
      this.alarmInfos = [];
      this.model = cloneDeep(model);
      const { sn, taskId, rangeDateVal, playSpeed } = model;
      const $map = this.$refs.deviceMap;
      if (this.taskId) {
        $map.reset();
        this.unsubMsg();
      }
      $map.sns = sn;
      this.taskId = taskId;
      try {
        const params = validParamObject(
          {
            sns: sn,
            taskId,
            beginTime: rangeDateVal[0],
            endTime: rangeDateVal[1],
            playSpeed
          },
          'String,Array'
        );
        const res = await replay(params);
        const { centerPosition, alarmBeginTime, alarmInfos, devices } = res.data ? res.data : {};
        const coordinates = [];
        let coordinate;
        const pois = devices.map(e => {
          coordinate = [e.longitude, e.latitude];
          coordinates.push(coordinate);
          return {
            state: e.state,
            pointCoordinate: coordinate,
            sn: e.sn,
            runstate: e.runstate,
            data: 0
          };
        });
        const time = toTimestamp(alarmBeginTime);
        const data = { deviceData: pois, max: 10, time: formatTime(time), pointData: [] };
        this.dataCache = {};
        this.dataCache[time] = data;
        $map.upBasicData(data);

        if (coordinates.length >= 3) {
          let points = convexClosure(coordinates);
          points = polygonScale(points, 0.00081);
          $map.drawPolygon(points.concat([points[0]]));
        }

        centerPosition && $map.animation(centerPosition);

        this.getDevicemapParam(taskId);
        this.alarmInfos = alarmInfos;
        this.replaying = true;
        this.subscribe();
      } finally {
        this.loading = false;
      }
    },
    setMapConfig(config) {
      if (!isObject(config)) return;
      this.rawMapConfig = config;
      const { deviceMapParamItems } = config;

      if (!isArray(deviceMapParamItems)) return;
      const temp = {};
      deviceMapParamItems.forEach(item => {
        if (item.itemCode === 'gradient') {
          temp[item.itemCode] = JSON.parse(item.value);
        } else if (item.itemCode === 'max') {
          temp[item.itemCode] = this.max || +item.value;
        } else {
          temp[item.itemCode] = item.value;
        }
      });
      this.mapConfig = temp;
    },
    async getDevicemapParam(taskId) {
      const { data } = (await getDevicemapParam(taskId)) || {};
      this.setMapConfig(data);
    },
    alarmReplay(model) {
      this.search({ ...this.model, ...model });
    },
    setLogTime(v) {
      this.logTime = toTimestamp(v);
    },
    subscribe() {
      if (!this.subscribeKey) return;
      subscribe(this.subscribeKey);
      this.sockets.subscribe(this.subscribeKey, res => {
        if (!res || !res.time) return;
        const timestamp = toTimestamp(res.time);
        this.dataCache[timestamp] = res;
        this.cacheRanges.splice(0, 1, [this.range[0], timestamp]);
      });
    },
    unsubMsg() {
      stopReplay();
    },
    unsubscribe() {
      this.sockets.unsubscribe(this.subscribeKey);
    }
  },
  beforeDestroy() {
    this.loading = false;
    this.unsubscribe();
    this.unsubMsg();
  }
};
</script>

<style lang="scss">
.task-replay-wrap {
  height: 100%;
  .replay-map {
    height: calc(100% - 80px) !important;
  }
  .control-wrap {
    position: absolute;
    z-index: 10;
    right: 30px;
    top: 30px;
    text-align: right;

    .block {
      display: flex;
      align-items: center;
      width: 140px;
      .label {
        display: inline-block;
        color: #49c7c3;
        min-width: 48px;
        text-align: right;
      }
      .radius-slider {
        flex: 1;
      }
    }
  }
}
</style>
